/* eslint-disable react-hooks/rules-of-hooks */
import PriceRowLoyaltyPoints from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPoints'
import PriceRowLoyaltyPointsPromoPill from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPointsPromoPill'
import PriceRowLoyaltyPointsLoadingSkeleton from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPointsLoadingSkeleton'
import { useAppSelector } from 'hooks/reduxHooks'
import { nonNullable } from 'lib/array/arrayUtils'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import determineLoyaltyBonusPointsCampaignType from 'loyalty/lib/determineLoyaltyBonusPointsCampaignType'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import React, { ComponentProps, useContext, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { triplePointsTakeoverId } from 'takeovers/societyTriplePointsTakeover/societyTriplePointsTakeoverConfig'
import GeoContext from 'contexts/geoContext'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import { SOCIETE_DOUBLE_POINTS_TAKEOVER_ID } from 'takeovers/societeDoublePointsTakeover/constants/societeDoublePointsTakeoverConstants'

type InheritedPriceRowLoyaltyPointsProps = Pick<ComponentProps<typeof PriceRowLoyaltyPoints>, 'onInfoIconClick' | 'calculationType' | 'horizontalAlign'>

interface Props extends InheritedPriceRowLoyaltyPointsProps {
  calculationRequests: Array<App.LoyaltyPointsEarnCalculationRequest | undefined> | App.LoyaltyPointsEarnCalculationRequest | undefined
  /**
   * @default 'S'
   */
  size?: React.ComponentProps<typeof PriceRowLoyaltyPoints>['size']
  /**
   * @default false
   */
  hideStatusCredits?: boolean
  /**
   * @default false
   */
  hidePromoPill?: boolean
  offer?: App.Offer | App.HotelOffer | App.HotelOfferSummary | App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary
}

function _LoyaltyPointsEarn(props: Props) {
  const {
    calculationRequests,
    size = 'S',
    hideStatusCredits = false,
    hidePromoPill = false,
    calculationType,
    horizontalAlign,
    offer,
    onInfoIconClick,
  } = props
  const { currentRegionCode } = useContext(GeoContext)
  const accountRegion = useAppSelector(getLoyaltyAccountRegion)
  const siteTakeover = useCurrentSiteTakeover({ offer })
  const triplePointsCampaign = siteTakeover?.id === triplePointsTakeoverId
  const doublePointsCampaign = siteTakeover?.id === SOCIETE_DOUBLE_POINTS_TAKEOVER_ID

  const loyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const displayTier = useAppSelector(getLoyaltyDisplayTier)

  const [loyaltyPointsRef, loyaltyPointsInView] = useInView({
    triggerOnce: true,
    rootMargin: '400px 0px 400px 0px',
  })

  const { totalPointsCalculation } = useLoyaltyPointsEarnCalculator(calculationRequests, {
    skipAll: !loyaltyPointsInView,
  })

  const initial = totalPointsCalculation?.initial
  const fetching = !!totalPointsCalculation?.fetching
  const err = totalPointsCalculation?.error
  const points = totalPointsCalculation?.data?.points ?? 0
  const pointsEarnRatio = totalPointsCalculation?.data?.roundedPointsEarnRatio ?? 0
  const campaignTypeFromCalculation = useMemo(() =>
    totalPointsCalculation?.data ? determineLoyaltyBonusPointsCampaignType(totalPointsCalculation.data) : [],
  [totalPointsCalculation?.data])

  const bonusPointsCampaignTypes = useMemo(() => {
    // Return all campaigns to display multiple pills
    if (campaignTypeFromCalculation?.length > 0) return campaignTypeFromCalculation
    const estimatedCampaigns: Array<App.LoyaltyBonusPointsCampaignType> = []
    if (calculationType === 'estimate' && offer && triplePointsCampaign) {
      estimatedCampaigns.push('triple')
    }
    if (calculationType === 'estimate' && offer && doublePointsCampaign) {
      estimatedCampaigns.push('double_status_credits')
    }
    return estimatedCampaigns.length > 0 ? estimatedCampaigns : undefined
  }, [campaignTypeFromCalculation, calculationType, offer, triplePointsCampaign, doublePointsCampaign])
  const trailingCaption = useMemo<string | undefined>(() => {
    if (!campaignTypeFromCalculation?.length && calculationType === 'estimate' && offer && triplePointsCampaign) {
      switch (offer.type) {
        case 'hotel':
        case 'tactical_ao_hotel':
          return '(select packages)'
        case 'tour':
        case 'direct_tour':
        case 'partner_tour':
        case 'connection_tour':
          return '(select departures)'
      }
    }
  }, [calculationType, offer, campaignTypeFromCalculation, triplePointsCampaign])
  const statusCredits = (totalPointsCalculation?.data?.statusCredits ?? 0) + (totalPointsCalculation?.data?.bonusStatusCredits ?? 0)

  if (
    !loyaltyEnabled ||
    !calculationRequests ||
    (Array.isArray(calculationRequests) && nonNullable(calculationRequests).length === 0) ||
    err ||
    (!initial && !fetching && !points) ||
    (!!accountRegion && accountRegion !== currentRegionCode)
  ) return null
  return <div ref={loyaltyPointsRef}>
    {(fetching || initial) && <PriceRowLoyaltyPointsLoadingSkeleton tier={displayTier} size={size} />}
    {!fetching && !initial && <>
      {(!bonusPointsCampaignTypes || hidePromoPill) && <PriceRowLoyaltyPoints
        tier={displayTier}
        earnablePoints={points}
        size={size}
        calculationType={calculationType}
        statusCredits={hideStatusCredits ? undefined : statusCredits}
        pointsEarnRatio={pointsEarnRatio}
        horizontalAlign={horizontalAlign}
        onInfoIconClick={onInfoIconClick}
      />}
      {!!bonusPointsCampaignTypes && !hidePromoPill && <PriceRowLoyaltyPointsPromoPill
        campaigns={bonusPointsCampaignTypes}
        tier={displayTier}
        earnablePoints={points}
        size={size}
        calculationType={calculationType}
        statusCredits={hideStatusCredits ? undefined : statusCredits}
        pointsEarnRatio={pointsEarnRatio}
        horizontalAlign={horizontalAlign}
        trailingCaption={trailingCaption}
        onPillClick={onInfoIconClick}
      />}
    </>}
  </div>
}

export default _LoyaltyPointsEarn
